import type {
  RoastChartPoint,
  RoastExtraColumnKey,
  RoastPhaseArea,
  RoastProfileEdit,
  RoastProfileEditableKey,
  RoastProfileRow,
  RoastStageMarker,
  RoastStageState,
} from './types';

export const extraColumnOptions: { key: RoastExtraColumnKey; label: string }[] = [
  { key: 'airTemp', label: 'Air Temp' },
  { key: 'drumTemp', label: 'Drum Temp' },
  { key: 'environmentTemp', label: 'Environment Temp' },
  { key: 'drumRpm', label: 'Drum RPM' },
];

export const AGTRON_RANGES = [
  { min: 90, common: 'Light', scaa: 'Extremely Light' },
  { min: 80, common: 'Cinnamon', scaa: 'Very Light' },
  { min: 70, common: 'Medium', scaa: 'Light' },
  { min: 60, common: 'High', scaa: 'Medium Light' },
  { min: 50, common: 'City', scaa: 'Medium' },
  { min: 40, common: 'Full City', scaa: 'Medium Dark' },
  { min: 30, common: 'French', scaa: 'Dark' },
  { min: 0, common: 'Italian', scaa: 'Very Dark' },
];

export const getAgtronClassification = (value: string | number) => {
  if (value === '' || value === null || value === undefined) return { common: 'N/A', scaa: 'N/A' };
  const normalizedValue = typeof value === 'string' ? value.replace(',', '.').trim() : value;
  const v = Number(normalizedValue);
  if (!Number.isFinite(v)) return { common: 'N/A', scaa: 'N/A' };
  if (v < 0 || v > 100) return { common: 'N/A', scaa: 'N/A' };
  const found = AGTRON_RANGES.find(r => v >= r.min);
  return found ? { common: found.common, scaa: found.scaa } : { common: 'N/A', scaa: 'N/A' };
};


/** Highest temperature a roaster can plausibly report, in °C. */
export const MAX_ROAST_TEMP_C = 550;

/**
 * Caps a temperature typed into the roast profile at {@link MAX_ROAST_TEMP_C}.
 * A slip like "10000000000" snaps down to 550 instead of flattening the chart,
 * while empty or half-typed input ("", "-", "5.") passes through so typing
 * never fights the caret.
 */
export const clampTemperatureInput = (value: string): string => {
  if (value.trim() === '') return value;
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return value;
  return parsed > MAX_ROAST_TEMP_C ? String(MAX_ROAST_TEMP_C) : value;
};

/** Column order used wherever edits are listed, so the ledger reads like the table. */
export const EDITABLE_SERIES_KEYS: RoastProfileEditableKey[] = [
  'bean',
  'fire',
  'airflow',
  'airTemp',
  'drumTemp',
  'environmentTemp',
  'drumRpm',
];

/** i18n key suffix under `roast.profileTable` for each editable column. */
export const EDITABLE_SERIES_LABEL_KEY: Record<RoastProfileEditableKey, string> = {
  bean: 'temp',
  fire: 'fire',
  airflow: 'airflow',
  airTemp: 'airTemp',
  drumTemp: 'drumTemp',
  environmentTemp: 'environmentTemp',
  drumRpm: 'drumRpm',
};

export const buildEditKey = (seconds: number, key: RoastProfileEditableKey) => `${seconds}:${key}`;

const normalizeCell = (value: number | null | undefined): number | null =>
  value === null || value === undefined ? null : value;

export const rowHasData = (row: RoastProfileRow) => {
  return (
    row.bean != null ||
    row.ror != null ||
    row.fire != null ||
    row.airflow != null ||
    row.airTemp != null ||
    row.drumTemp != null ||
    row.environmentTemp != null ||
    row.drumRpm != null
  );
};

/**
 * Compare the live rows against the profile as imported and report every cell that
 * differs. Rows the person added by hand carry their own delete control, so an empty
 * one is not reported here; only cells sitting on an imported timestamp are diffed,
 * plus any value typed into a row the import never had.
 */
export const diffProfileAgainstBaseline = (
  rows: RoastProfileRow[],
  baseline: RoastProfileRow[]
): RoastProfileEdit[] => {
  if (baseline.length === 0) return [];

  const baselineBySeconds = new Map(baseline.map(r => [r.seconds, r]));
  const edits: RoastProfileEdit[] = [];

  for (const row of rows) {
    const base = baselineBySeconds.get(row.seconds);
    if (!base && !rowHasData(row)) continue;

    for (const key of EDITABLE_SERIES_KEYS) {
      const current = normalizeCell(row[key]);
      const original = normalizeCell(base?.[key]);
      if (current === original) continue;

      edits.push({ seconds: row.seconds, time: row.time, key, baseline: original, current });
    }
  }

  return edits;
};

export const stripTrailingEmptyRows = (rows: RoastProfileRow[]): RoastProfileRow[] => {
  let lastIndexWithData = -1;
  rows.forEach((row, idx) => {
    if (rowHasData(row)) {
      lastIndexWithData = idx;
    }
  });

  if (lastIndexWithData === -1) {
    if (rows.length === 0) return [];
    return [rows[0]];
  }

  return rows.slice(0, lastIndexWithData + 1);
};

export const buildEmptyRowAtIndex = (index: number): RoastProfileRow => {
  const sec = index * 30;
  const m = Math.floor(sec / 60);
  const s = sec % 60;

  return {
    time: `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`,
    seconds: sec,
    bean: null,
    ror: null,
    fire: null,
    airflow: null,
  };
};

export const ensureSingleTrailingRow = (rows: RoastProfileRow[]): RoastProfileRow[] => {
  const trimmed = stripTrailingEmptyRows(rows);

  if (trimmed.length === 0) {
    return [buildEmptyRowAtIndex(0)];
  }

  const nextIndex = trimmed.length;
  return [...trimmed, buildEmptyRowAtIndex(nextIndex)];
};

export const calculateRorForRows = (rows: RoastProfileRow[]): RoastProfileRow[] => {
  const copy = rows.map(r => ({ ...r, ror: null as number | null }));

  // Identify 30s-interval rows by seconds value
  const thirtySecRows: { originalIndex: number; nonManualOrder: number }[] = [];
  rows.forEach((r, idx) => {
    if (r.seconds % 30 === 0) {
      thirtySecRows.push({ originalIndex: idx, nonManualOrder: thirtySecRows.length });
    }
  });

  // Calculate ROR between pairs of 30s-interval rows (every 2 rows = 1 minute)
  for (let k = 2; k < thirtySecRows.length; k += 2) {
    const startEntry = thirtySecRows[k - 2];
    const endEntry = thirtySecRows[k];
    const rorEntry = thirtySecRows[k - 1];

    if (!startEntry || !endEntry || !rorEntry) continue;

    const startBean = copy[startEntry.originalIndex]?.bean;
    const endBean = copy[endEntry.originalIndex]?.bean;

    if (startBean !== null && endBean !== null && startBean !== undefined && endBean !== undefined) {
      const diff = endBean - startBean;
      if (diff < 0) {
        copy[rorEntry.originalIndex].ror = null;
      } else {
        copy[rorEntry.originalIndex].ror = Number(diff.toFixed(1));
      }
    } else {
      copy[rorEntry.originalIndex].ror = null;
    }
  }

  return copy;
};

export const parseTime = (timeStr: string) => {
  if (!timeStr) return null;
  const [mmStr, ssStr] = timeStr.split(':');
  const mm = Number((mmStr ?? '').replace(/\D/g, ''));
  const ss = Number((ssStr ?? '').replace(/\D/g, ''));
  if (isNaN(mm) || isNaN(ss)) return null;
  return mm + ss / 60;
};

export const formatTime = (totalSeconds: number) => {
  const m = Math.floor(totalSeconds / 60);
  const s = Math.floor(totalSeconds % 60);
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
};

export const calculateChartData = (rows: RoastProfileRow[], stages?: RoastStageState[]): RoastChartPoint[] => {
  const result: RoastChartPoint[] = [];
  let lastFire: number | null = null;
  let lastAirflow: number | null = null;
  let lastAirTemp: number | null = null;
  let lastDrumTemp: number | null = null;
  let lastEnvTemp: number | null = null;
  let lastDrumRpm: number | null = null;

  type DataPoint = {
    seconds: number;
    row?: RoastProfileRow;
    stage?: RoastStageState;
  };

  const combinedPoints: DataPoint[] = rows.map(r => ({ seconds: r.seconds, row: r }));

  if (stages) {
    stages.forEach(s => {
      const t = parseTime(s.time);
      if (t !== null && s.temp !== '') {
        const sec = Math.round(t * 60);
        const existing = combinedPoints.find(p => p.seconds === sec);
        if (existing) {
          existing.stage = s;
        } else {
          combinedPoints.push({ seconds: sec, stage: s });
        }
      }
    });
  }

  combinedPoints.sort((a, b) => a.seconds - b.seconds);

  let lastBeanIndex = -1;
  for (let i = rows.length - 1; i >= 0; i--) {
    if (rows[i].bean !== null && rows[i].bean !== undefined) {
      lastBeanIndex = rows[i].seconds;
      break;
    }
  }
  
  if (stages) {
    stages.forEach(s => {
      if (s.temp !== '') {
        const t = parseTime(s.time);
        if (t !== null) {
          const sec = Math.round(t * 60);
          if (sec > lastBeanIndex) lastBeanIndex = sec;
        }
      }
    });
  }

  for (let i = 0; i < combinedPoints.length; i++) {
    const p = combinedPoints[i];
    const r = p.row;
    const s = p.stage;

    if (r) {
      if (r.fire !== null && r.fire !== undefined) lastFire = r.fire;
      if (r.airflow !== null && r.airflow !== undefined) lastAirflow = r.airflow;
      if (r.airTemp !== null && r.airTemp !== undefined) lastAirTemp = r.airTemp;
      if (r.drumTemp !== null && r.drumTemp !== undefined) lastDrumTemp = r.drumTemp;
      if (r.environmentTemp !== null && r.environmentTemp !== undefined) lastEnvTemp = r.environmentTemp;
      if (r.drumRpm !== null && r.drumRpm !== undefined) lastDrumRpm = r.drumRpm;
    }

    const isWithinRange = lastBeanIndex === -1 || p.seconds <= lastBeanIndex;

    result.push({
      timeStr: r?.time || s?.time || '', 
      time: p.seconds / 60,
      bean: r?.bean ?? (s?.temp !== '' ? Number(s?.temp) : null),
      ror: r?.ror ?? null,
      fire: isWithinRange ? lastFire : null,
      airflow: isWithinRange ? lastAirflow : null,
      airTemp: isWithinRange ? (r?.airTemp ?? lastAirTemp) : null,
      drumTemp: isWithinRange ? (r?.drumTemp ?? lastDrumTemp) : null,
      environmentTemp: isWithinRange ? (r?.environmentTemp ?? lastEnvTemp) : null,
      drumRpm: isWithinRange ? (r?.drumRpm ?? lastDrumRpm) : null,
    });
  }

  return result;
};

export const calculatePhaseAreas = (stages: RoastStageState[], maxChartTime: number) => {
  if (!stages || stages.length === 0) return [];

  const timeToSeconds = (timeStr: string) => {
    const [mmStr, ssStr] = timeStr.split(':');
    const mm = Number((mmStr ?? '').replace(/\D/g, ''));
    const ss = Number((ssStr ?? '').replace(/\D/g, ''));
    return (Number.isFinite(mm) ? mm : 0) * 60 + (Number.isFinite(ss) ? ss : 0);
  };

  const calculateDuration = (startKey: string, endKey: string) => {
    const startStage = stages.find(s => s.key === startKey);
    const endStage = stages.find(s => s.key === endKey);
    if (!startStage || !endStage || !startStage.time || !endStage.time) return '00:00';
    const startSec = timeToSeconds(startStage.time);
    const endSec = timeToSeconds(endStage.time);
    if (endSec <= startSec) return '00:00';
    return formatTime(endSec - startSec);
  };

  const calculatePhaseRatio = (phaseStartKey: string, phaseEndKey: string) => {
    const startStage = stages.find(s => s.key === 'start');
    const endStage = stages.find(s => s.key === 'end');
    const phaseStartStage = stages.find(s => s.key === phaseStartKey);
    const phaseEndStage = stages.find(s => s.key === phaseEndKey);

    if (!startStage || !endStage || !phaseStartStage || !phaseEndStage || !startStage.time || !endStage.time || !phaseStartStage.time || !phaseEndStage.time) return '0.00';

    const totalSec = timeToSeconds(endStage.time) - timeToSeconds(startStage.time);
    const phaseSec = timeToSeconds(phaseEndStage.time) - timeToSeconds(phaseStartStage.time);

    if (totalSec <= 0 || phaseSec <= 0) return '0.00';

    return ((phaseSec / totalSec) * 100).toFixed(2);
  };

  const dryingTime = calculateDuration('start', 'yellow');
  const maillardTime = calculateDuration('yellow', 'first_crack');
  const developmentTime = calculateDuration('first_crack', 'end');
  const dryingRatio = calculatePhaseRatio('start', 'yellow');
  const maillardRatio = calculatePhaseRatio('yellow', 'first_crack');
  const developmentRatio = calculatePhaseRatio('first_crack', 'end');

  const getStageTime = (key: string) => {
    const stage = stages.find((s) => s.key === key);
    return stage ? parseTime(stage.time) : null;
  };

  const startTime = getStageTime('start') ?? 0;
  const yellowTime = getStageTime('yellow');
  const firstCrackTime = getStageTime('first_crack');
  const endTime = getStageTime('end');

  const effectiveYellow = yellowTime ?? (startTime !== null ? maxChartTime : null);
  const effectiveFirstCrack = firstCrackTime ?? (yellowTime !== null ? maxChartTime : null);
  const effectiveEnd = endTime ?? (firstCrackTime !== null ? maxChartTime : null);

  const areas: RoastPhaseArea[] = [];

  if (effectiveYellow !== null && effectiveYellow > startTime) {
    areas.push({
      x1: startTime,
      x2: effectiveYellow,
      fill: '#fde047',
      opacity: 0.3,
      label: 'Drying',
      detail: `${dryingTime} | ${dryingRatio}%`,
    });
  }

  if (yellowTime !== null && effectiveFirstCrack !== null && effectiveFirstCrack > yellowTime) {
    areas.push({
      x1: yellowTime,
      x2: effectiveFirstCrack,
      fill: '#f59e0b',
      opacity: 0.3,
      label: 'Maillard',
      detail: `${maillardTime} | ${maillardRatio}%`,
    });
  }

  if (firstCrackTime !== null && effectiveEnd !== null && effectiveEnd > firstCrackTime) {
    areas.push({
      x1: firstCrackTime,
      x2: effectiveEnd,
      fill: '#b45309',
      opacity: 0.3,
      label: 'Development',
      detail: `${developmentTime} | ${developmentRatio}%`,
    });
  }
  
  return areas;
};

export const calculateStageMarkers = (stages: RoastStageState[]): RoastStageMarker[] => {
  if (!stages || stages.length === 0) return [];

  const markers = stages.map((s): RoastStageMarker | null => {
    if (s.temp === '') return null; 
    
    const labelTop = `${s.temp}°`;
    let labelBottom = '';
    
    const timeStr = s.time;
    const timeSec = parseTime(timeStr);
    if (timeSec === null) return null;
    
    if (s.key === 'start') labelBottom = `CHARGE`;
    else if (s.key === 'turning_point') labelBottom = `TP ${timeStr}`;
    else if (s.key === 'yellow') labelBottom = `Yellow ${timeStr}`;
    else if (s.key === 'first_crack') labelBottom = `FCs ${timeStr}`;
    else if (s.key === 'end') labelBottom = `DROP ${timeStr}`;
    
    return {
      key: s.key,
      time: timeSec,
      temp: Number(s.temp),
      labelTop,
      labelBottom,
    };
  });

  return markers.filter((m): m is RoastStageMarker => m !== null);
};

export const calculateXTicks = (chartData: RoastChartPoint[], stages?: RoastStageState[]) => {
  let maxTime = 0;
  
  if (chartData.length > 0) {
    maxTime = Math.ceil(chartData[chartData.length - 1].time);
  }

  if (stages) {
    stages.forEach(s => {
      const t = parseTime(s.time);
      if (t !== null && t > maxTime) {
        maxTime = Math.ceil(t);
      }
    });
  }

  if (maxTime === 0 && chartData.length === 0) maxTime = 10; 

  const ticks = [];
  for (let t = 0; t <= maxTime + 1; t += 0.5) {
    ticks.push(t);
  }
  return ticks;
};

export const calculateTempTicks = (tempAxisMax: number) => {
  const ticks = new Set<number>([-5]);
  if (tempAxisMax > 0) {
    const stepRaw = tempAxisMax / 4;
    const roundToNice = (value: number) => {
      const rounded = Math.round(value / 5) * 5;
      return rounded > 0 ? rounded : Math.ceil(value);
    };

    for (let i = 1; i <= 3; i += 1) {
      const tick = roundToNice(stepRaw * i);
      if (tick > 0 && tick < tempAxisMax) {
        ticks.add(tick);
      }
    }
    ticks.add(tempAxisMax);
  }
  return Array.from(ticks).sort((a, b) => a - b);
};
